import React, { useCallback, useEffect, useMemo } from "react";
import {
  InclusionContent,
  ProductTypeColor,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import { graphql, HeadProps, PageProps } from "gatsby";
import { CruiseQuery, SitePageContextCruise } from "types/graphql-types";
import CruiseInfoSummary from "src/page-components/cruise/CruiseInfoSummary/CruiseInfoSummary";
import CruiseItinerary from "src/page-components/cruise/itinerary/CruiseItinerary";
import useCruisePrice from "src/ssc-api/prices/useCruisePrice";
import CruiseSectionOffers from "../page-components/cruise/SectionOffers";
import SectionItineraryContent from "../page-components/cruise/SectionItineraryContent";
import SectionSuites from "../page-components/cruise/suites/SectionSuites";
import SectionShip from "../page-components/cruise/ship/SectionShip";
import {
  buildMenuSections,
  buildPdfPath,
  collectCruiseSlideshow,
  getShorexesActiveOnFares
} from "../page-components/cruise/helpers";
import ErrorBoundary from "components/ErrorBoundary";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { legacyUrl } from "src/utils/routing/legacyUrl";
import {
  buildCruiseName,
  buildNonBreakingCruiseName,
  cruiseIsWaitlist,
  cruiseTypeTransformer,
  includesSilverseaHotel,
  isRealPort
} from "src/utils/cruise";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import {
  buildItineraryWithExcursions,
  buildPrePost,
  useRemoteGetExcursions
} from "src/page-components/cruise/itinerary/Utils";
import { getCruiseSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { voyageToDataLayer } from "src/utils/analytics/tracking";
import { arrayIsNotEmpty, isPresent } from "src/utils/checks";
import PrePostProgrammes from "src/page-components/cruise/PrePostProgrammes/PrePostProgrammes";

import { useECommerceStatus } from "src/ssc-api/useECommerceStatus";
import type { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import type { FareCode } from "@silversea-ssc/web-fares";
import GatsbyHeadOpenGraph from "components/SEO/GatsbyHeadOpenGraph";
import { buildSEOImageUrl } from "components/SEO/utils";
import { PageContext } from "types/pageContext";
import { languageFromCode } from "src/utils/localization/languages/languages";
import { useSendUserEvent } from "src/utils/useSendUserEvent";
import {
  ActivePromoForCruise,
  activePromosForCruise,
  activePromosVisibleIn
} from "src/utils/promos";
import LastSeenProducts from "components/LastSeenProducts/LastSeenProducts";
import { useLastSeenProducts } from "components/LastSeenProducts/useLastSeenProducts";
import StickyCruiseMenuNew from "src/page-components/cruise/StickyCruiseMenu/StickyCruiseMenuNew";
import { scrollToSectionId } from "src/utils/inPageNavigation";
import { buildCruiseJsonLd } from "../page-components/cruise/schemaOrgJsonLd";
import SectionBenefits from "../page-components/cruise/SectionBenefits";
import CruiseFares from "features/fare/infographic/CruiseFares/CruiseFares";
import { cruiseDayByDay } from "src/page-components/cruise/itinerary/day-by-day";
import { infographicConfigForCruiseGroup } from "features/fare/infographic/infographic-config-for-cruise-group";
import { BlockContent } from "components/BlockContent";
import { ExcursionsMicroContentProvider } from "src/page-components/cruise/ExcursionsMicroContentProvider";
import { useKeepBooking } from "components/KeepBooking/useKeepBooking";
import { buildCruisePageVisited } from "@silversea-ssc/dotcom-api-types/UserEvents";
import { hasMandatoryFields } from "../utils/pre-post-programme";
import PageBuilderEmotionalLauncher from "components/PageBuilder/PageBuilderEmotionalLauncher";
import { AlgoliaAnalyticsParamsProviderFromURL } from "components/Algolia/AlgoliaAnalyticsProvider";

const CruiseUrgencyBox = React.lazy(
  () => import("components/UrgencyBox/CruiseUrgencyBox")
);

const CruiseOfferUrgencyBox = React.lazy(
  () => import("components/UrgencyBox/CruiseOfferUrgencyBox")
);

const ExpeditionTeamMembers = React.lazy(
  () => import("src/page-components/cruise/ExpeditionTeamMembers")
);

const BlogSection = React.lazy(
  () => import("src/page-components/cruise/BlogSection")
);

const CruiseEvents = React.lazy(
  () => import("src/page-components/cruise/cruise-events/CruiseEvents")
);

// NOTE: a cruise page without data, destination and code is not generated
export type CruiseQueryPageProps = CruiseQuery & {
  cruise: NonNullable<CruiseQuery["cruise"]> & {
    data: NonNullable<NonNullable<CruiseQuery["cruise"]>["data"]> & {
      cruiseType: "Classic" | "Expedition";
      destination: NonNullable<
        NonNullable<NonNullable<CruiseQuery["cruise"]>["data"]>["destination"]
      >;
      cruiseCode: string;
    };
  };
};

const orderActivePromosForUrgencyBoxAndGetFirst = (
  promos: ActivePromoForCruise[]
) => {
  const manualPromos = promos.filter(
    (promo) => promo.content.configuration?.promoType === "manual"
  );
  const otherPromos = promos.filter(
    (promo) => promo.content.configuration?.promoType !== "manual"
  );
  const sortedPromos = [...otherPromos, ...manualPromos];
  return sortedPromos[0] ?? null;
};

const CruiseTemplate: React.FC<
  React.PropsWithChildren<
    PageProps<CruiseQueryPageProps, { cruise: SitePageContextCruise }>
  >
> = (props) => {
  const cruise = props.data.cruise;
  const cruiseData = cruise.data;
  const cruiseCode = cruiseData.cruiseCode;

  const cruiseTags = cruise.cruiseTags
    .map((tag) => tag.localized)
    .filter(isPresent);

  const language = useLanguage();

  const keepBookingResponse = useKeepBooking();

  const cruisePrices = useCruisePrice(cruiseCode, language);
  const geoLocation = useGeolocation();
  const price = cruisePrices?.priceFrom;

  const { nextCruiseI18nPath = undefined, prevCruiseI18nPath = undefined } =
    props.pageContext.cruise || {};

  const itinerary = (cruiseData.itinerary || []).filter(isPresent);
  const ship = cruiseData.ship;
  const shipVersion = cruiseData.shipVersion;
  const nextCruisePagePath = legacyUrl(nextCruiseI18nPath);
  const prevCruisePagePath = legacyUrl(prevCruiseI18nPath);

  const portCodes = itinerary
    ?.map((i) => i?.port?._id)
    ?.filter(isPresent)
    ?.map((portId) => portId.replace(/drafts./, ""));

  //TODO partially rewrite as resolver 12
  const { data: excursionsData } = useRemoteGetExcursions(
    cruiseCode,
    portCodes
  );

  const { shorexes, confirmedExcursions } = excursionsData || {
    shorexes: [],
    confirmedExcursions: []
  };

  const shorexesActiveOnFares = useMemo(() => {
    return getShorexesActiveOnFares(cruisePrices?.fares || []);
  }, [cruisePrices?.fares]);

  const itineraryWithExcursions = useMemo(() => {
    return buildItineraryWithExcursions(
      itinerary,
      shorexes,
      confirmedExcursions
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(confirmedExcursions)]);

  const inclusionsContent = (cruise.inclusionsContent?.filter(isPresent) ||
    []) as InclusionContent[];

  const cruiseDayByDays = cruiseDayByDay(
    cruisePrices?.fares || [],
    infographicConfigForCruiseGroup(
      // TODO: avoid type cast. Improve cruiseData types
      cruiseData.cruiseGroup as CruiseGroupCode | undefined
    ),
    cruiseData.departureDate
  );

  const preItinerary = buildPrePost(cruiseDayByDays.pre, inclusionsContent);
  const postItinerary = buildPrePost(cruiseDayByDays.post, inclusionsContent);

  const preHotelLand =
    cruiseData.preHotelLand?.filter(isPresent).filter(hasMandatoryFields) || [];
  const postHotelLand =
    cruiseData.postHotelLand?.filter(isPresent).filter(hasMandatoryFields) ||
    [];
  const withPrePost = preHotelLand?.length > 0 || postHotelLand?.length > 0;
  const cruiseEvents = cruise.events?.filter(isPresent);
  const expeditionTeam = cruise.expeditionTeam?.filter(isPresent);
  const withExpeditionTeam = arrayIsNotEmpty(expeditionTeam);
  const withEvents = arrayIsNotEmpty(cruiseEvents);
  const blogSectionContent = cruise.blog;
  const withSilverseaHotel =
    includesSilverseaHotel(preHotelLand, postHotelLand) &&
    isPresent(
      cruiseData.destination.silverseaHotelSectionContent?.emotionalSection
    );

  const withStories =
    blogSectionContent && blogSectionContent.blogPosts?.length > 0;

  const menuSections = buildMenuSections({
    hasExcursions: cruise.hasExcursions,
    withExpeditionTeam,
    withEvents,
    withSilverseaHotel,
    withPrePost,
    withShip: true,
    withStories
  });

  const cruiseSlideshow = React.useMemo(() => {
    // TODO: rewrite as graphql resolver
    return collectCruiseSlideshow(cruise) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cruiseName = buildCruiseName(
    cruise.data.departurePort?.data?.name?.localized,
    cruise.data.arrivalPort?.data?.name?.localized
  );
  const cruiseTitle = buildNonBreakingCruiseName(
    cruise.data.departurePort?.data?.name?.localized,
    cruise.data.arrivalPort?.data?.name?.localized
  );
  const pdfPath = buildPdfPath(props.location, geoLocation.countryCode);
  const raqPagePath = props.data.raqPagePath;
  const cruiseSpecificRAQUrl =
    raqPagePath && cruiseCode
      ? getCruiseSpecificRAQ(raqPagePath, cruiseCode)
      : undefined;

  const ecommerceStatus = useECommerceStatus(
    cruiseCode,
    cruiseIsWaitlist(cruisePrices)
  );

  const shortDestinationName = cruiseData.destination.card?.title?.localized;
  const destinationNameWithPrefix =
    `${cruiseData.destination.prefixArticle?.localized || ""} ${shortDestinationName}`.trim();

  useEffect(() => {
    voyageToDataLayer({
      name: cruiseName,
      code: cruiseCode,
      departure_date: cruiseData.departureDate,
      arrival_date: cruiseData.arrivalDate,
      days_duration: cruiseData.days,
      ship_name: cruiseData.ship?.name,
      price: price?.type == "price" ? price?.amount : undefined,
      sale_state: price?.type,
      departure_port: cruiseData.departurePort?.data?.code,
      arrival_port: cruiseData.arrivalPort?.data?.code,
      destination_id: cruiseData.destination.destinationId,
      destination_name: cruiseData.destination.name?.en,
      cruise_type: cruiseData.cruiseType
    });
  }, [
    cruiseName,
    cruise,
    price,
    cruiseCode,
    cruiseData.departureDate,
    cruiseData.arrivalDate,
    cruiseData.days,
    cruiseData.ship?.name,
    cruiseData.departurePort?.data?.code,
    cruiseData.arrivalPort?.data?.code,
    cruiseData.cruiseType,
    cruiseData.destination.destinationId,
    cruiseData.destination.name?.en
  ]);

  // const infographicCruisePackages = cruise.packages?.filter(
  //   cruiseSpecialPackageFilterByCountry(geoLocation)
  // );

  const onOfferRequestWithScroll = useCallback((_promoCode: string) => {
    scrollToSectionId("offers-slider");
  }, []);

  useSendUserEvent(buildCruisePageVisited(cruiseCode));

  const activePromos = useMemo(
    () =>
      activePromosForCruise(
        cruisePrices?.webPromotions || [],
        props.data.promos.nodes
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(cruisePrices?.webPromotions)]
  );

  const activePromosForHeroContent = activePromosVisibleIn(
    activePromos,
    "cruisePageHero"
  );

  const activePromosForFareTabContent = activePromosVisibleIn(
    activePromos,
    "cruiseFareTab"
  );

  const activePromoForUrgencyBox = activePromosVisibleIn(
    activePromos,
    "urgencyBox"
  );

  const activePromoForOffersSlider = activePromosVisibleIn(
    activePromos,
    "cruiseOfferSliderCard"
  );

  const withOfferUrgencyBox = arrayIsNotEmpty(activePromoForUrgencyBox);

  const activePromoForUrgencyBoxContent =
    orderActivePromosForUrgencyBoxAndGetFirst(activePromoForUrgencyBox);

  const keepBookingActive = isPresent(keepBookingResponse);

  const { hasLastSeenProducts, ...lastSeenProducts } = useLastSeenProducts();
  return (
    <div>
      <AlgoliaAnalyticsParamsProviderFromURL objectID={cruiseCode}>
        <ExcursionsMicroContentProvider
          content={props.data.excursionsMicroContent}
        >
          <ProductTypeColor
            productType={cruiseTypeTransformer(cruise.data.cruiseType)}
          >
            <CruiseInfoSummary
              pagePath={props.location.pathname}
              pdfPath={pdfPath}
              cruiseName={cruiseTitle}
              prices={cruisePrices}
              map={cruise.mapImage}
              destinationName={cruiseData.destination.name?.localized}
              destinationFullPath={cruiseData.destination.fullPath}
              cruiseType={cruiseData.cruiseType}
              departureDate={cruiseData.departureDate}
              arrivalDate={cruiseData.arrivalDate}
              duration={cruiseData.days}
              shipName={cruiseData.ship?.name}
              cruiseTags={cruiseTags}
              cruiseCode={cruiseCode}
              nextCruisePathByShip={nextCruisePagePath}
              prevCruisePathByShip={prevCruisePagePath}
              raqUrl={cruiseSpecificRAQUrl}
              ecommerceStatus={ecommerceStatus}
              themedCruises={cruise.themedCruises}
              activePromos={activePromosForHeroContent}
              eCommerceBenefitsContent={
                props.data.microContent?.eCommerceBenefitsContent
              }
            />

            <StickyCruiseMenuNew
              sectionRefs={menuSections}
              translateDesktopTop={64}
              spacer={false}
              summary={{
                cruiseName: cruiseName,
                cruiseCode: cruiseCode,
                prices: cruisePrices,
                departureDate: cruiseData.departureDate,
                arrivalDate: cruiseData.arrivalDate,
                days: cruiseData.days,
                raqUrl: cruiseSpecificRAQUrl,
                ecommerceStatus
              }}
            >
              Upper menu
            </StickyCruiseMenuNew>

            <div id="itinerary">
              <SectionItineraryContent
                cruise={cruise}
                hasExcursions={cruise.hasExcursions}
                slideshow={cruiseSlideshow}
                showVSPreSaleMessage={cruise.showVSPreSaleMessage}
                ecommerceStatus={ecommerceStatus}
              />

              <SectionContainer
                spacingTop="regular"
                spacingBottom="regular"
                backgroundColor="white"
              >
                <CruiseItinerary
                  itinerary={itineraryWithExcursions}
                  hideShorexesOnFirstDay={true}
                  hideShorexesOnLastDay={true}
                  itineraryDisclaimer={
                    props.data.microContent?.itineraryShorexDisclaimer
                      ?.localized
                  }
                  overrideItineraryDisclaimer={
                    cruise.overrideItineraryDisclaimer?.localized
                  }
                  defaultPortImage={props.data.site?.defaultPortImage}
                  preItinerary={preItinerary}
                  postItinerary={postItinerary}
                  tokenValues={cruisePrices?.tokens}
                  shorexesActiveOnFares={shorexesActiveOnFares}
                  cruiseType={
                    cruiseData.cruiseType == "Classic"
                      ? "classic"
                      : "expedition"
                  }
                />
              </SectionContainer>
            </div>

            <div id="offers">
              <CruiseFares
                cruiseCode={cruiseCode}
                cancellationPolicyModalContent={
                  props.data.cancellationPolicyModalContent!
                }
                inclusionsContent={inclusionsContent}
                cruisePrices={cruisePrices}
                faresContent={props.data.faresContent.nodes}
                cruiseGroup={cruiseData.cruiseGroup as CruiseGroupCode}
                fareCodes={cruiseData.fareCodes as FareCode[]}
                //cruisePackages={infographicCruisePackages}
                sectionHeading={props.data.microContent?.fareSection}
                raqUrl={cruiseSpecificRAQUrl}
                ecommerceStatus={ecommerceStatus}
                farePricesValidUntilDate={cruise.farePricesValidUntilDate}
                farePricesMayIncreaseOnDate={cruise.farePricesMayIncreaseOnDate}
                backgroundColor="neutral10"
                activePromos={activePromosForFareTabContent}
                onPromoBadgeClicked={onOfferRequestWithScroll}
                pricesValidUntilContent={
                  props.data.microContent?.pricesValidUntil?.localized
                }
              />

              <div id="offers-slider">
                <ErrorBoundary>
                  <CruiseSectionOffers
                    cruiseFares={cruiseData.fareCodes as FareCode[]}
                    activePromos={activePromoForOffersSlider}
                    ecommerceStatus={ecommerceStatus}
                  />
                </ErrorBoundary>
              </div>
            </div>

            {withExpeditionTeam && (
              <React.Suspense>
                <ExpeditionTeamMembers
                  id="expeditionTeam"
                  title={
                    props.data.microContent?.expeditionTeamSection?.title
                      ?.localized
                  }
                  content={
                    props.data.microContent?.expeditionTeamSection?.content
                      ?.localized
                  }
                  expeditionTeam={expeditionTeam}
                />
              </React.Suspense>
            )}

            {withEvents && (
              <React.Suspense>
                <CruiseEvents
                  id="events"
                  events={cruiseEvents}
                  backgroundColor={
                    withExpeditionTeam ? "neutral10" : "neutral05"
                  }
                  title={
                    props.data.microContent?.eventsSection?.title?.localized
                  }
                  content={
                    props.data.microContent?.eventsSection?.content?.localized
                  }
                />
              </React.Suspense>
            )}

            <div id="suites">
              {isPresent(ship) && isPresent(shipVersion) && (
                <SectionSuites
                  ship={ship}
                  shipVersion={shipVersion}
                  cruiseCode={cruiseCode}
                  cruisePrice={cruisePrices}
                  ecommerceStatus={ecommerceStatus}
                  activePromos={activePromos}
                  backgroundColor="productTypeColor02"
                  raqPagePath={raqPagePath}
                  title={
                    props.data.microContent?.suitesSection?.title?.localized
                  }
                >
                  <BlockContent
                    value={
                      props.data.microContent?.suitesSection?.content?.localized
                    }
                  />
                </SectionSuites>
              )}
            </div>
            {arrayIsNotEmpty(cruise.allInclusiveBenefits) && (
              <div id="benefits">
                <SectionBenefits
                  title={
                    props.data.microContent?.onboardBenefitsSection?.title
                      ?.localized
                  }
                  content={
                    props.data.microContent?.onboardBenefitsSection?.content
                      ?.localized
                  }
                  allInclusiveBenefits={cruise.allInclusiveBenefits}
                />
              </div>
            )}
            {isPresent(ship) && isPresent(shipVersion) && (
              <div id="ship">
                <SectionShip
                  ship={ship}
                  shipVersion={shipVersion}
                  ecommerceStatus={ecommerceStatus}
                />
              </div>
            )}
            {withSilverseaHotel && (
              <div id="silversea-hotel">
                <React.Suspense>
                  <PageBuilderEmotionalLauncher
                    {...cruiseData.destination.silverseaHotelSectionContent
                      ?.emotionalSection}
                  />
                </React.Suspense>
              </div>
            )}
            {withPrePost && (
              <div id="prePost">
                <PrePostProgrammes
                  preFallbackImage={(itinerary[0].port?.slideshow || [])[0]}
                  postFallbackImage={
                    (itinerary[itinerary.length - 1].port?.slideshow || [])[0]
                  }
                  preProgrammes={preHotelLand}
                  postProgrammes={postHotelLand}
                />
              </div>
            )}
            {blogSectionContent && blogSectionContent.blogPosts?.length > 0 && (
              <React.Suspense>
                <BlogSection
                  withPrePost={withPrePost}
                  blog={blogSectionContent}
                />
              </React.Suspense>
            )}

            {hasLastSeenProducts && (
              <SectionContainer
                backgroundColor="neutral05"
                spacingTop="condensed"
                spacingBottom="condensed"
                dataContentName="last-seen-product-section"
              >
                <LastSeenProducts
                  cardBackgroundColor="white"
                  {...lastSeenProducts}
                />
              </SectionContainer>
            )}

            <React.Suspense>
              {withOfferUrgencyBox && !keepBookingActive ? (
                <CruiseOfferUrgencyBox
                  offerContent={activePromoForUrgencyBoxContent}
                  ecommerceUrl={ecommerceStatus}
                />
              ) : (
                <CruiseUrgencyBox
                  destinationName={destinationNameWithPrefix}
                  destinationWebCode={cruiseData.destination.destinationWebCode}
                  ecommerceUrl={ecommerceStatus}
                />
              )}
            </React.Suspense>
          </ProductTypeColor>
        </ExcursionsMicroContentProvider>
      </AlgoliaAnalyticsParamsProviderFromURL>
    </div>
  );
};

export const Head = (props: HeadProps<CruiseQueryPageProps, PageContext>) => {
  const {
    data,
    pageContext,
    location: { pathname }
  } = props;
  const language = languageFromCode(pageContext.languageCode);
  const cruise = data.cruise;
  const cruiseData = cruise.data;
  const cruiseCode = cruiseData.cruiseCode;
  const openGraph = cruise.overriddenOpenGraph;
  const seoImage = buildSEOImageUrl(openGraph?.image || cruise.cardImage);

  return (
    <GatsbyHeadOpenGraph
      path={props.location.pathname}
      alternateLinks={pageContext.alternateLinks}
      languageCode={pageContext.languageCode}
      openGraph={openGraph}
      altImage={seoImage}
      excludeFromSitemap={cruise.metadata?.excludeFromSitemap}
      jsonLd={buildCruiseJsonLd({
        language: language,
        image: seoImage,
        name: openGraph?.title?.localized,
        pathname: pathname,
        departureDate: cruiseData.departureDate,
        arrivalDate: cruiseData.arrivalDate,
        departureCityName: cruiseData.departurePort?.data?.name?.localized,
        arrivalCityName: cruiseData.arrivalPort?.data?.name?.localized,
        cruiseCode: cruiseData.cruiseCode,
        description: openGraph?.description?.localized,
        destinationName: cruiseData.destination.name?.localized,
        destinationUrl: cruiseData.destination.fullPath,
        itinerary: (cruiseData.itinerary || [])
          .filter((x) => isRealPort(x?.port?.data?.code))
          .map((x) =>
            x?.port?.data?.name?.localized
              ? { name: x?.port?.data?.name?.localized }
              : undefined
          )
          .filter(isPresent)
      })}
    >
      <link
        rel="prefetch"
        href={`${process.env.GATSBY_DIGITAL_API_BASE_URL}/v2/cruisePriceAndOffers/${cruiseCode}?language=${language.sanityCode}`}
        as="fetch"
        type="application/json"
      />
    </GatsbyHeadOpenGraph>
  );
};

export const query = graphql`
  fragment CruiseFareInfographicContent on SanityFarePage {
    fareCodes
    ...InfographicBoxContent
  }

  fragment CruisePackage on SanityPackage {
    _id
    locationFilters {
      ...AirNonAirOrLocationFilter
    }

    cruiseInfographicCard {
      title {
        localized
      }
      subtitle {
        localized
      }
      disclaimer {
        localized
      }
    }

    page {
      ... on SanityPage {
        fullPath
        id
      }

      ... on SanityLandingPage {
        fullPath
        id
      }
    }
  }

  fragment CruiseMap on CruiseMapImage {
    type
    image {
      ...Image
    }
  }

  fragment ThemedCruise on SanityThemedCruise {
    code
    name {
      localized
    }
  }

  fragment ExpeditionTeamMember on SanityExpeditionTeamMember {
    _id
    picture {
      ...ImageWithAlt
    }
    title {
      localized
    }
    name
    shortBio {
      localized
    }
  }

  query Cruise($id: String!) {
    raqPagePath
    cruise: sanityCruise(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
      }

      overriddenOpenGraph {
        ...OpenGraphMetaTags
      }

      cardImage {
        ...Image
      }

      cruiseTags {
        localized
      }

      hasExcursions
      showVSPreSaleMessage
      farePricesValidUntilDate
      farePricesMayIncreaseOnDate

      themedCruises {
        ...ThemedCruise
      }
      inclusionsContent {
        ...InfographicStepInclusionContent
      }

      itineraryDescription {
        localized
      }

      overrideItineraryDisclaimer {
        localized
      }

      mapImage {
        ...CruiseMap
      }

      blog {
        ...PageBuilderBlogSection
      }

      packages {
        ...CruisePackage
      }

      events {
        ...CruiseEvent
      }

      allInclusiveBenefits {
        _id
        icon
        title {
          localized
        }
        inclusions {
          _key
          text {
            localized
          }
          highlight
        }
      }

      expeditionTeam {
        ...ExpeditionTeamMember
      }

      data {
        cruiseCode
        cruiseType
        departureDate
        arrivalDate
        days
        fareCodes
        cruiseGroup

        departurePort {
          data {
            code
            name {
              localized
            }
          }
        }
        arrivalPort {
          data {
            code
            name {
              localized
            }
          }
        }

        destination {
          fullPath
          destinationId
          destinationWebCode
          name {
            en
            localized
          }
          card {
            title {
              localized
            }
          }
          prefixArticle {
            localized
          }
          silverseaHotelSectionContent {
            emotionalSection {
              ...PageBuilderEmotionalLauncher
            }
          }
        }

        ship {
          ...CruiseSectionShip
        }
        shipVersion {
          ...CruiseSectionShipVersion
        }

        itinerary {
          ...CruiseItinerary
          ...CruiseItineraryPortSlideshow
        }

        postHotelLand {
          ...HotelOrLandProgramme
        }

        preHotelLand {
          ...HotelOrLandProgramme
        }
      }
    }

    microContent: cruisePageMicroContent {
      eCommerceBenefitsContent {
        ...ECommerceBenefitsContent
      }
      fareSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      pricesValidUntil {
        localized
      }

      itineraryShorexDisclaimer {
        localized
      }

      suitesSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      onboardBenefitsSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      eventsSection {
        title {
          localized
        }
        content {
          localized
        }
      }

      expeditionTeamSection {
        title {
          localized
        }
        content {
          localized
        }
      }
    }

    excursionsMicroContent: excursionsMicroContent {
      ...MicroContentExcursions
    }

    cancellationPolicyModalContent: sanityCancellationPolicyModal(
      _id: { regex: "/cancellationPolicyModal$/" }
    ) {
      ...CancellationPolicyModalContent
    }

    faresContent: allSanityFarePage {
      nodes {
        ...CruiseFareInfographicContent
      }
    }

    site: sanitySite(_id: { eq: "site-silversea-com" }) {
      defaultPortImage {
        ...ImageWithAlt
      }
    }

    promos: allSanityExclusiveOfferPerPromo {
      nodes {
        ...CruiseOfferContent
      }
    }
  }
`;

export default CruiseTemplate;
